/**
 * FeedbackTab Component
 * Displays feedback events (thumbs up/down and satisfaction responses) from Application Insights
 */

import { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, CheckCircle, AlertCircle, XCircle, Minus, RefreshCw, MessageSquare } from 'react-feather';
import { DateRange } from '../../types/logs';

interface FeedbackTabProps {
  dateRange: DateRange;
}

interface FeedbackEvent {
  id: string;
  timestamp: string;
  conversationId: string;
  type: 'thumbs' | 'satisfaction';
  rating?: 'positive' | 'negative';
  comment?: string;
  messageId?: string;
  satisfactionResponse?: 'yes' | 'partial' | 'no' | 'skip';
  user?: {
    email?: string;
    name?: string;
    orgId?: string;
  };
  contentSource: {
    type: 'direct' | 'widget' | 'api' | 'teams';
    domain?: string;
  };
}

interface FeedbackResult {
  events: FeedbackEvent[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  summary: {
    totalThumbsUp: number;
    totalThumbsDown: number;
    totalSatisfactionYes: number;
    totalSatisfactionPartial: number;
    totalSatisfactionNo: number;
    totalSatisfactionSkip: number;
  };
}

type FeedbackType = 'all' | 'thumbs' | 'satisfaction';

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function FeedbackTab({ dateRange }: FeedbackTabProps) {
  const [data, setData] = useState<FeedbackResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('all');

  useEffect(() => {
    fetchFeedback();
  }, [dateRange, feedbackType]);

  async function fetchFeedback() {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (dateRange.startDate) params.set('startDate', dateRange.startDate.toISOString());
      if (dateRange.endDate) params.set('endDate', dateRange.endDate.toISOString());
      if (feedbackType !== 'all') params.set('feedbackType', feedbackType);

      const response = await fetch(`/api/activity-logs/feedback?${params}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to fetch feedback');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-gray-400" size={24} />
        <span className="ml-2 text-gray-500">Loading feedback...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        <AlertCircle className="mx-auto mb-2" size={24} />
        <p>{error}</p>
        <button
          onClick={fetchFeedback}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const summary = data?.summary || {
    totalThumbsUp: 0,
    totalThumbsDown: 0,
    totalSatisfactionYes: 0,
    totalSatisfactionPartial: 0,
    totalSatisfactionNo: 0,
    totalSatisfactionSkip: 0
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-2">
            <ThumbsUp className="text-green-500" size={20} />
            <span className="text-sm text-gray-600">Thumbs Up</span>
          </div>
          <p className="text-2xl font-semibold mt-1">{summary.totalThumbsUp}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-2">
            <ThumbsDown className="text-red-500" size={20} />
            <span className="text-sm text-gray-600">Thumbs Down</span>
          </div>
          <p className="text-2xl font-semibold mt-1">{summary.totalThumbsDown}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="text-green-500" size={20} />
            <span className="text-sm text-gray-600">Satisfied</span>
          </div>
          <p className="text-2xl font-semibold mt-1">{summary.totalSatisfactionYes}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="text-yellow-500" size={20} />
            <span className="text-sm text-gray-600">Partial</span>
          </div>
          <p className="text-2xl font-semibold mt-1">{summary.totalSatisfactionPartial}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-2">
            <XCircle className="text-red-500" size={20} />
            <span className="text-sm text-gray-600">Not Satisfied</span>
          </div>
          <p className="text-2xl font-semibold mt-1">{summary.totalSatisfactionNo}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-2">
            <Minus className="text-gray-400" size={20} />
            <span className="text-sm text-gray-600">Skipped</span>
          </div>
          <p className="text-2xl font-semibold mt-1">{summary.totalSatisfactionSkip}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <div className="flex gap-2">
            {(['all', 'thumbs', 'satisfaction'] as FeedbackType[]).map(type => (
              <button
                key={type}
                onClick={() => setFeedbackType(type)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  feedbackType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type === 'all' ? 'All' : type === 'thumbs' ? 'Thumbs Up/Down' : 'Satisfaction'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Feedback List */}
      {!data?.events?.length ? (
        <div className="text-center py-12 text-gray-500">
          <MessageSquare className="mx-auto mb-2" size={32} />
          <p>No feedback found in the selected time range</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Response
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.events.map((event) => (
                <tr key={event.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTimestamp(event.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      event.type === 'thumbs' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {event.type === 'thumbs' ? 'Thumbs' : 'Satisfaction'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {event.type === 'thumbs' ? (
                      event.rating === 'positive' ? (
                        <span className="inline-flex items-center text-green-600">
                          <ThumbsUp size={16} className="mr-1" /> Positive
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-red-600">
                          <ThumbsDown size={16} className="mr-1" /> Negative
                        </span>
                      )
                    ) : (
                      <span className={`inline-flex items-center ${
                        event.satisfactionResponse === 'yes' ? 'text-green-600' :
                        event.satisfactionResponse === 'partial' ? 'text-yellow-600' :
                        event.satisfactionResponse === 'no' ? 'text-red-600' : 'text-gray-400'
                      }`}>
                        {event.satisfactionResponse === 'yes' && <CheckCircle size={16} className="mr-1" />}
                        {event.satisfactionResponse === 'partial' && <AlertCircle size={16} className="mr-1" />}
                        {event.satisfactionResponse === 'no' && <XCircle size={16} className="mr-1" />}
                        {event.satisfactionResponse === 'skip' && <Minus size={16} className="mr-1" />}
                        {event.satisfactionResponse}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {event.user?.email || event.user?.name || 'Anonymous'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                    {event.comment || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                      {event.contentSource.type}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination info */}
      {data && data.totalPages > 1 && (
        <div className="text-center text-sm text-gray-500">
          Page {data.currentPage} of {data.totalPages} ({data.totalCount} total)
        </div>
      )}
    </div>
  );
}
